import { useAuth } from './useAuth';
import { Permission, SubscriptionPlan } from '../types/auth';
import PermissionService from '../services/PermissionService';

/**
 * Hook for checking user permissions
 * @returns Permission checking functions
 */
const usePermission = () => {
  const { role, subscriptionPlan, isAuthenticated } = useAuth();

  /**
   * Check if the current user has a specific permission
   * @param permission - The permission to check
   * @returns boolean indicating if the user has the permission
   */
  const hasPermission = (permission: Permission): boolean => {
    return PermissionService.hasPermission(role, permission);
  };

  /**
   * Check if the current user has a subscription-based permission
   * @param permission - The permission to check
   * @returns boolean indicating if the user has the permission
   */
  const hasSubscriptionPermission = (permission: Permission): boolean => {
    return PermissionService.hasSubscriptionPermission(
      subscriptionPlan,
      permission
    );
  };

  /**
   * Check if the current user has full permission (both role and subscription)
   * @param permission - The permission to check
   * @returns boolean indicating if the user has the permission
   */
  const hasFullPermission = (permission: Permission): boolean => {
    return PermissionService.hasFullPermission(
      role,
      subscriptionPlan,
      permission
    );
  };

  /**
   * Check if the current user has all of the specified permissions
   * @param permissions - Array of permissions to check
   * @returns boolean indicating if the user has all permissions
   */
  const hasAllPermissions = (permissions: Permission[]): boolean => {
    return permissions.every((permission) => hasFullPermission(permission));
  };

  /**
   * Check if the current user has any of the specified permissions
   * @param permissions - Array of permissions to check
   * @returns boolean indicating if the user has at least one permission
   */
  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return permissions.some((permission) => hasFullPermission(permission));
  };

  /**
   * Check if the current user has premium access
   * @returns boolean indicating if the user has premium access
   */
  const isPremium = (): boolean => {
    return PermissionService.hasPremiumAccess(subscriptionPlan);
  };

  /**
   * Check if the current user has pro access
   * @returns boolean indicating if the user has pro access
   */
  const isPro = (): boolean => {
    return PermissionService.hasProAccess(subscriptionPlan);
  };

  /**
   * Check if the current user is on trial
   * @returns boolean indicating if the user is on trial
   */
  const isOnTrial = (): boolean => {
    return PermissionService.isOnTrial(subscriptionPlan);
  };

  /**
   * Check if the current user is on free tier
   * @returns boolean indicating if the user is on free tier
   */
  const isFree = (): boolean => {
    return subscriptionPlan === SubscriptionPlan.FREE;
  };

  /**
   * Check if the current user has pay-per-scan access
   * @returns boolean indicating if the user has pay-per-scan access
   */
  const hasPayPerScan = (): boolean => {
    return subscriptionPlan === SubscriptionPlan.PAY_PER_SCAN;
  };

  /**
   * Check if the current user is authenticated (not a guest)
   * @returns boolean indicating if the user is authenticated
   */
  const isLoggedIn = (): boolean => {
    return isAuthenticated;
  };

  /**
   * Check if the current user is a guest (unauthenticated)
   * @returns boolean indicating if the user is a guest
   */
  const isGuest = (): boolean => {
    return !isAuthenticated;
  };

  /**
   * Get the subscription tier name for display
   * @returns string name of the subscription tier
   */
  const getSubscriptionTierName = (): string => {
    return PermissionService.getSubscriptionTierName(subscriptionPlan);
  };

  return {
    hasPermission,
    hasSubscriptionPermission,
    hasFullPermission,
    hasAllPermissions,
    hasAnyPermission,
    isPremium,
    isPro,
    isOnTrial,
    isFree,
    hasPayPerScan,
    isLoggedIn,
    isGuest,
    getSubscriptionTierName,
    role,
    subscriptionPlan,
  };
};

export default usePermission;
